'use client';
import { lockBodyScroll } from '@/lib/bodyScrollLock';

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plane } from 'lucide-react';
import { PhillipsScrew } from '@/components/chrome/PhillipsScrew';
import { AmtilSymbol } from '@/components/chrome/AmtilSymbol';

/**
 * The single AMTIL "beefed-up" beveled modal shell. Reuses the shared
 * `.amtil-plate-card` machined-bevel + corner-cut treatment (globals.css) plus
 * the real brand assets (AmtilSymbol, PhillipsScrew) so every modal in the app
 * reads as one industrial system — never a bespoke per-modal restyle.
 *
 * Feedback tickets: 214e15df (Related Terms), d9fb482a (Log Book / Study Notes /
 * Report Issues), 62b0d5fb (Reference), c230edd4 (tour). Consumers pass their
 * body as children; the shell owns backdrop, portal, ESC/scroll-lock, header,
 * on-brand close button and the corner screw-heads.
 */

/** True when `el` is the last open modal dialog in the document (portals append in open order). */
export function isTopmostDialog(el: HTMLElement | null): boolean {
  if (!el) return true;
  const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
  return dialogs[dialogs.length - 1] === el;
}

export type PlateStyle = 'brushed' | 'anodized' | 'polished';
const PLATE_STYLES: readonly PlateStyle[] = ['brushed', 'anodized', 'polished'];

export interface BevelModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Show the real AMTIL symbol beside the title (per 214e15df annotation spec). */
  showSymbol?: boolean;
  /** Embed micro screw-heads in the plate corners. */
  screws?: boolean;
  /**
   * Logo-plate variant (director 2026-09-21): rounded corners instead of the
   * machined corner-cut, four quite-small screw-heads. Opt-in so existing
   * modals are untouched until promoted.
   */
  rounded?: boolean;
  /**
   * Logo-plate finish (director 2026-09-21, "give me a few variants"):
   * a very subtle metallic sheen + its own entrance animation + screw pattern.
   *   brushed  — fine horizontal brushing, 4 corner screws, settle-in entrance
   *   anodized — smooth satin gradient, 6 screws on wide plates, drop-in entrance
   *   polished — one specular sweep on open, 2 top screws, focus-in entrance
   * Only meaningful with `rounded`. When omitted the plate is flat and the
   * prototype's `data-plate-style` root attribute may still drive the finish.
   */
  plateStyle?: PlateStyle;
  maxWidth?: number | string;
  /** Omit the header bar entirely (title lives in the body); the consumer must then render its own close (e.g. in the footer). */
  hideHeader?: boolean;
  children: React.ReactNode;
  /** Optional sticky footer region (e.g. pagination) rendered below the body. */
  footer?: React.ReactNode;
  /**
   * Primary action blended into the plate's BOTTOM-RIGHT corner (director
   * annotation 2026-09-22), mirroring the DEPART button top-right. Pass a
   * `<Link>`/`<button>` carrying `className="amtil-corner-action"`.
   */
  cornerAction?: React.ReactNode;
  /** Extra style for the scrollable body. */
  bodyStyle?: React.CSSProperties;
  labelledById?: string;
}

/** On-brand close button: a solid machined steel plate (no translucent glass),
 *  deep-navy glyph, cta-red on hover. Replaces the off-brand red glass box.
 *  Glyph = the assessments "DEPART" plane (director 2026-09-21: every X
 *  adopts the depart-plane icon from the flashcards). */
export function BevelCloseButton({
  onClose,
  label = 'Close',
  corner = false,
  buttonRef,
}: {
  onClose: () => void;
  label?: string;
  /** Blend flush into the plate's top-right corner, sharing its radius. */
  corner?: boolean;
  buttonRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={buttonRef}
      onClick={onClose}
      aria-label={label}
      className={corner ? 'amtil-corner-close' : undefined}
      style={{
        // Director 2026-09-26: the standard exit key app-wide, 20% smaller than the
        // former 44/36px (glyph 18→14px).
        minWidth: corner ? '35px' : '29px',
        minHeight: corner ? '35px' : '29px',
        padding: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Director 2026-09-21: DEPART is red in its standby state (assessments
        // --as-depart), brighter on hover — never grey-at-rest.
        color: '#fdf3c0',
        background: 'linear-gradient(180deg, #c0181d 0%, #a11216 100%)',
        border: '1px solid #6e0b0e',
        borderRadius: '6px',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(0,0,0,0.35)',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.filter = 'brightness(1.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.filter = 'none';
      }}
    >
      <Plane aria-hidden="true" className="bevel-close-plane" style={{ width: '14px', height: '14px', transform: 'rotate(45deg)' }} />
    </button>
  );
}

/**
 * The ONE close position for every plate in the app (director 2026-09-22:
 * "adopt the blended exit corner button on every modal throughout the app").
 * Drop it as the first child of any `position: relative` dialog surface with
 * `overflow: hidden`; it takes the surface's own corner radius via `inherit`.
 */
// Two DEPART sizes (director 2026-09-26): the default suits small modals (related terms,
// pickers); `size="lg"` is for large surfaces such as the image gallery.
export function BevelCornerClose({ onClose, label, buttonRef, size = 'sm' }: { onClose: () => void; label?: string; buttonRef?: React.Ref<HTMLButtonElement>; size?: 'sm' | 'lg' }) {
  return (
    <div data-testid="bevel-close-rail" className={`amtil-corner-rail${size === 'lg' ? ' depart-lg' : ''}`}>
      <BevelCloseButton onClose={onClose} label={label} corner buttonRef={buttonRef} />
    </div>
  );
}

export default function BevelModal({
  isOpen,
  onClose,
  title,
  subtitle,
  showSymbol = false,
  screws = false,
  rounded = false,
  plateStyle,
  hideHeader = false,
  maxWidth = 640,
  children,
  footer,
  cornerAction,
  bodyStyle,
  labelledById,
}: BevelModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    // Stacked modals (e.g. the 3D peek over a preview): only the topmost dialog
    // answers Escape, and each layer restores the scroll-lock value it found.
    const handleEscape = (e: KeyboardEvent) => {
      // A control inside (e.g. an open CockpitSelect) that consumed Escape marks it defaultPrevented.
      if (e.key === 'Escape' && !e.defaultPrevented && isTopmostDialog(dialogRef.current)) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  // Scroll hold keyed on isOpen only: consumers pass inline onClose arrows, and re-running
  // the lock on every render would unlock → scroll → relock each keystroke (Opus 2026-09-28).
  useEffect(() => {
    if (!isOpen) return;
    return lockBodyScroll();
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  // Finish resolution: explicit prop, else the prototype's root attribute
  // (`<html data-plate-style>` — the /prototype variant picker), else flat.
  const rootFinish = document.documentElement.dataset.plateStyle as PlateStyle | undefined;
  const finish: PlateStyle | undefined = plateStyle ?? (rootFinish && PLATE_STYLES.includes(rootFinish) ? rootFinish : undefined);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(2, 34, 51, 0.7)',
        backdropFilter: 'blur(2px)',
        // The modal layer (--z-modal 1200): above --z-overlay surfaces such as the chat widget,
        // which painted over every dialog while this sat at 1000 (rev 9.1 deviation C3).
        zIndex: 'var(--z-modal)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        animation: 'fadeIn 0.2s ease-out',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        className={
          rounded
            ? `amtil-plate-card amtil-plate-card--rounded amtil-modal-shape${finish ? ` amtil-plate--${finish}` : ''}`
            : 'amtil-plate-card amtil-modal-shape'
        }
        data-plate-style={rounded ? finish : undefined}
        style={{
          position: 'relative',
          backgroundColor: 'var(--bg-surface)',
          width: '100%',
          maxWidth,
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          // Rounded plates take their entrance from the finish (globals.css)
          ...(rounded ? {} : { animation: 'slideUp 0.3s ease-out' }),
          ...(rounded
            ? {
                clipPath: 'none',
                borderRadius: 14,
                overflow: 'hidden',
                boxShadow:
                  'inset 0 1.5px 0 rgba(255,255,255,0.9), inset 0 -2px 0 rgba(2,34,51,0.10), 0 0 0 1px var(--border-strong), 0 24px 48px -12px rgba(2,34,51,0.45)',
              }
            : {}),
        }}
      >
        {/* Rounded logo-plates carry NO screw/rivet heads (director 2026-09-21). */}
        {screws && !rounded && (
          <>
            <PhillipsScrew size={9} style={{ top: 9, left: 9 }} />
            <PhillipsScrew size={9} style={{ top: 9, right: 9 }} />
            {/* bottom-left kept clear of the plate's corner-cut (bottom-right) */}
            <PhillipsScrew size={9} style={{ bottom: 9, left: 9 }} />
          </>
        )}

        {/* ONE close position, every plate (director 2026-09-22: "constant UI
            placement of our close/depart everywhere ... same location on every
            type of card or modal"). The DEPART button lives at the TOP-RIGHT of
            the plate. Header-less layouts get this slim rail instead of hiding
            the control or pushing it into a footer, so it never sits on top of
            the content (the 3D-model overlap the director rejected). */}
        <BevelCornerClose onClose={onClose} label={typeof title === 'string' && title ? `Close dialog: ${title}` : undefined} />

        {/* Compact header — shrunk vs the old oversized bar to reclaim real estate */}
        {!hideHeader && (
        <div
          className="amtil-corner-clear"
          data-modal-header=""
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            // Title-only headers sit at the exit key's height (director 2026-09-26).
            padding: subtitle || showSymbol ? '12px 16px' : '6px 16px',
            minHeight: subtitle || showSymbol ? undefined : 'var(--depart-size-md, 36px)',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-inset)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            {showSymbol && <AmtilSymbol height={22} style={{ flexShrink: 0 }} />}
            <div style={{ minWidth: 0 }}>
              <h2
                id={labelledById}
                style={{
                  fontSize: '17px',
                  fontWeight: 800,
                  color: 'var(--heading)',
                  margin: 0,
                  lineHeight: 1.25,
                  letterSpacing: '-0.01em',
                }}
              >
                {title}
              </h2>
              {subtitle && (
                <div
                  style={{
                    color: 'var(--text-muted)',
                    fontSize: '12.5px',
                    margin: '2px 0 0',
                  }}
                >
                  {subtitle}
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Body. `minHeight: 0` lets this flex child actually shrink inside the
            88vh plate — without it a tall body overflows the plate instead of
            scrolling. Layouts that must not scroll (director 2026-09-22) size
            themselves to the viewport rather than clipping with overflow:hidden. */}
        <div style={{ overflowY: 'auto', minHeight: 0, padding: '20px', ...bodyStyle }}>{children}</div>

        {footer && (
          <div
            style={{
              borderTop: '1px solid var(--border)',
              padding: '12px 16px',
              background: 'var(--bg-inset)',
            }}
          >
            {footer}
          </div>
        )}

        {cornerAction && (
          <div data-testid="bevel-corner-action" className="amtil-corner-action-rail">
            {cornerAction}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
